import { useCallback, useEffect, useContext, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, {
  DropdownItemRadio,
  DropdownItemRadioGroup,
} from '@atlaskit/dropdown-menu';
import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import AppsIcon from '@atlaskit/icon/core/apps';
import BookWithBookmarkIcon from '@atlaskit/icon/core/book-with-bookmark';
import FolderOpenIcon from '@atlaskit/icon/core/folder-open';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import StarStarredIcon from '@atlaskit/icon/core/star-starred';
import { ContainerAvatar } from '@atlaskit/navigation-system/side-nav-items/container-avatar';
import { MenuList } from '@atlaskit/navigation-system/side-nav-items/menu-list';
import { token } from '@atlaskit/tokens';
import { GlobalSideNavigation } from '@atlassian/global-side-navigation';
import {
  ItemsConfig,
  ForYouConfig,
} from '@atlassian/global-side-navigation/types';

import { MenuItem } from 'src/components/navigation/src/types';
import { RecentDataContext } from 'src/contexts/recent-data';
import { useRecentData } from 'src/hooks/recent-data';
import { useSetState } from 'src/hooks/set-state';
import { useUserWorkspacePref } from 'src/hooks/use-pref';
import { useLocation } from 'src/router/hooks';
import { settings } from 'src/settings';
import { publishUiEvent } from 'src/utils/analytics/publish';

import { getAppMenuItems } from './apps-folder';
import {
  getSideNavViewMode,
  PROJECTS_VIEW_MODE_PREFERENCE_KEY,
  REPOSITORIES_VIEW_MODE_PREFERENCE_KEY,
  SIDE_NAV_VIEW_MODES,
  type SideNavViewMode,
} from './constants';
import {
  RepositoryNavigationItem,
  ProjectNavigationItem,
} from './expandable-menu-item';
import {
  useMenuItems,
  useRepositoryNavigation,
  useProjectNavigation,
  useLoadRecents,
  useShowStarredObjects,
  useStarredOnboarding,
  useStarredFlyoutItems,
  StarredObjectType,
} from './hooks';
import { getIconForMenuItem } from './icons';
import { RecentEmptyState } from './recent-empty-state';
import { StarredEmptyState } from './starred-empty-state';
import { StarredItemsProvider } from './starred-items-context';
import { isMenuItemSelected } from './utils';

const REPOS_MENU_ITEM = 'repositories-tab';
const PROJECTS_MENU_ITEM = 'projects-tab';

const STARRED_FILTERS = {
  ALL: 'all',
  REPOSITORIES: 'repositories',
  PROJECTS: 'projects',
} as const;

const messages = defineMessages({
  customizeRepositoriesViewMode: {
    id: 'frontbucket.navSystem.sideNav.repositories.viewMode.customize',
    description:
      'Label for the repositories side nav view mode dropdown trigger',
    defaultMessage: 'Customize repositories view',
  },
  customizeProjectsViewMode: {
    id: 'frontbucket.navSystem.sideNav.projects.viewMode.customize',
    description: 'Label for the projects side nav view mode dropdown trigger',
    defaultMessage: 'Customize projects view',
  },
  repositoriesStarredAndRecentViewMode: {
    id: 'frontbucket.navSystem.sideNav.repositories.viewMode.starredAndRecent',
    description:
      'Dropdown option for showing both starred and recent repositories',
    defaultMessage: 'Starred and recent repositories',
  },
  repositoriesStarredOnlyViewMode: {
    id: 'frontbucket.navSystem.sideNav.repositories.viewMode.starredOnly',
    description: 'Dropdown option for showing only starred repositories',
    defaultMessage: 'Starred repositories only',
  },
  repositoriesMostRecentOnlyViewMode: {
    id: 'frontbucket.navSystem.sideNav.repositories.viewMode.mostRecentOnly',
    description: 'Dropdown option for showing only the most recent repository',
    defaultMessage: 'Most recent repository only',
  },
  projectsStarredAndRecentViewMode: {
    id: 'frontbucket.navSystem.sideNav.projects.viewMode.starredAndRecent',
    description: 'Dropdown option for showing both starred and recent projects',
    defaultMessage: 'Starred and recent projects',
  },
  projectsStarredOnlyViewMode: {
    id: 'frontbucket.navSystem.sideNav.projects.viewMode.starredOnly',
    description: 'Dropdown option for showing only starred projects',
    defaultMessage: 'Starred projects only',
  },
  projectsMostRecentOnlyViewMode: {
    id: 'frontbucket.navSystem.sideNav.projects.viewMode.mostRecentOnly',
    description: 'Dropdown option for showing only the most recent project',
    defaultMessage: 'Most recent project only',
  },
});

const sideNavViewModeActionLabels = {
  repositories: messages.customizeRepositoriesViewMode,
  projects: messages.customizeProjectsViewMode,
};

const sideNavViewModeOptionLabels = {
  repositories: {
    [SIDE_NAV_VIEW_MODES.STARRED_AND_RECENT]:
      messages.repositoriesStarredAndRecentViewMode,
    [SIDE_NAV_VIEW_MODES.STARRED_ONLY]:
      messages.repositoriesStarredOnlyViewMode,
    [SIDE_NAV_VIEW_MODES.MOST_RECENT_ONLY]:
      messages.repositoriesMostRecentOnlyViewMode,
  },
  projects: {
    [SIDE_NAV_VIEW_MODES.STARRED_AND_RECENT]:
      messages.projectsStarredAndRecentViewMode,
    [SIDE_NAV_VIEW_MODES.STARRED_ONLY]: messages.projectsStarredOnlyViewMode,
    [SIDE_NAV_VIEW_MODES.MOST_RECENT_ONLY]:
      messages.projectsMostRecentOnlyViewMode,
  },
};

const getSideNavViewModeOptions = (
  intl: ReturnType<typeof useIntl>,
  objectType: StarredObjectType
): {
  label: string;
  value: SideNavViewMode;
}[] => {
  const optionLabels = sideNavViewModeOptionLabels[objectType];

  return [
    {
      label: intl.formatMessage(
        optionLabels[SIDE_NAV_VIEW_MODES.STARRED_AND_RECENT]
      ),
      value: SIDE_NAV_VIEW_MODES.STARRED_AND_RECENT,
    },
    {
      label: intl.formatMessage(optionLabels[SIDE_NAV_VIEW_MODES.STARRED_ONLY]),
      value: SIDE_NAV_VIEW_MODES.STARRED_ONLY,
    },
    {
      label: intl.formatMessage(
        optionLabels[SIDE_NAV_VIEW_MODES.MOST_RECENT_ONLY]
      ),
      value: SIDE_NAV_VIEW_MODES.MOST_RECENT_ONLY,
    },
  ];
};

export const getSelectedStarredObjectType = (
  selectedFilters?: string[]
): StarredObjectType | undefined => {
  const selectedFilter = selectedFilters?.[0] ?? STARRED_FILTERS.ALL;

  return selectedFilter === STARRED_FILTERS.ALL
    ? undefined
    : (selectedFilter as StarredObjectType);
};

const useOptimisticSideNavViewMode = (
  preferenceKey: string,
  shouldLoadViewModePreference: boolean
) => {
  const [
    storedViewModePreference,
    updateStoredViewModePreference,
    isStoredViewModePreferenceLoading,
  ] = useUserWorkspacePref(preferenceKey, null, shouldLoadViewModePreference);

  // Preferences hydrate asynchronously and may be absent or invalid, so
  // normalize the stored value before using it in the UI.
  const savedViewMode = getSideNavViewMode(storedViewModePreference);
  const [pendingViewMode, setPendingViewMode] = useState<SideNavViewMode>();

  // While the saved preference is still loading, keep the section blank rather
  // than briefly rendering the default layout. If the user has already picked a
  // new option in this session, prefer that optimistic choice immediately.
  const isHydrating =
    shouldLoadViewModePreference &&
    isStoredViewModePreferenceLoading &&
    pendingViewMode === undefined;

  // Once the persisted preference catches up to the optimistic selection,
  // clear the local override so future renders read directly from storage.
  useEffect(() => {
    if (pendingViewMode === savedViewMode) {
      setPendingViewMode(undefined);
    }
  }, [pendingViewMode, savedViewMode]);

  const setViewMode = useCallback(
    (nextViewMode: SideNavViewMode) => {
      // Reflect the selection immediately, then persist it in the background.
      setPendingViewMode(nextViewMode);

      if (!shouldLoadViewModePreference) {
        return;
      }

      Promise.resolve(updateStoredViewModePreference(nextViewMode)).catch(
        () => {
          // If persistence fails, drop the optimistic override and fall back to
          // the last saved preference.
          setPendingViewMode(undefined);
        }
      );
    },
    [shouldLoadViewModePreference, updateStoredViewModePreference]
  );

  return [pendingViewMode ?? savedViewMode, setViewMode, isHydrating] as const;
};

const SideNavViewModeDropdownMenu = ({
  groupId,
  objectType,
  viewMode,
  onSelect,
}: {
  groupId: string;
  objectType: StarredObjectType;
  viewMode: SideNavViewMode;
  onSelect: (viewMode: SideNavViewMode) => void;
}) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const options = getSideNavViewModeOptions(intl, objectType);

  const handleOpenChange = useCallback(
    ({ isOpen: nextIsOpen }: { isOpen: boolean }) => {
      setIsOpen(nextIsOpen);
    },
    []
  );

  const handleSelect = useCallback(
    (nextViewMode: SideNavViewMode) => {
      if (nextViewMode !== viewMode) {
        publishUiEvent({
          action: 'updated',
          actionSubject: 'dropdown',
          actionSubjectId: 'sideNavViewModeDropdown',
          source: 'sideNav',
          attributes: {
            objectType,
            previousViewMode: viewMode,
            viewMode: nextViewMode,
          },
        });
      }
      onSelect(nextViewMode);
      setIsOpen(false);
    },
    [objectType, onSelect, viewMode]
  );

  return (
    <DropdownMenu
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      placement="right"
      shouldRenderToParent
      trigger={({ triggerRef, ...props }) => (
        <IconButton
          {...props}
          icon={() => <ShowMoreHorizontalIcon size="small" label="" />}
          label={intl.formatMessage(sideNavViewModeActionLabels[objectType])}
          appearance="subtle"
          spacing="compact"
          ref={triggerRef}
        />
      )}
    >
      <DropdownItemRadioGroup id={groupId}>
        {options.map(option => (
          <DropdownItemRadio
            key={option.value}
            id={option.value}
            isSelected={viewMode === option.value}
            onClick={() => handleSelect(option.value)}
          >
            {option.label}
          </DropdownItemRadio>
        ))}
      </DropdownItemRadioGroup>
    </DropdownMenu>
  );
};

type ForYouProps = {
  globalUrl: string;
  loadRepositories: () => any;
  allRecentItemsUrl?: string;
  isSelected: boolean;
  showStarred: boolean;
};

const forYouConfig = ({
  globalUrl,
  allRecentItemsUrl,
  loadRepositories,
  isSelected,
  showStarred,
}: ForYouProps): ForYouConfig => {
  return {
    forYouItem: {
      url: globalUrl,
      isSelected,
    },
    recent: {
      viewAllUrl: allRecentItemsUrl,
      emptyElem: <RecentEmptyState />,
      useItems: params => {
        const searchText = params?.searchText;

        const { isRepositoriesLoading, recentRepositories } =
          useContext(RecentDataContext);

        useEffect(() => {
          loadRepositories();
        }, []);

        let filteredRepos = recentRepositories;
        if (searchText) {
          filteredRepos = recentRepositories.filter(repo =>
            repo.name.toLowerCase().includes(searchText.toLowerCase())
          );
        }

        return {
          isLoading: isRepositoriesLoading,
          sections: [
            {
              items: filteredRepos.map(repo => ({
                primaryLabel: repo.name,
                href: repo.links.html.href.replace(settings().CANON_URL, ''),
                elemBefore: <ContainerAvatar src={repo.links.avatar.href} />,
              })),
            },
          ],
        };
      },
    },
    ...(showStarred && {
      starred: {
        emptyElem: <StarredEmptyState />,
        filterConfig: {
          defaultSelected: [STARRED_FILTERS.ALL],
          hideSelectedFilterTag: [
            STARRED_FILTERS.ALL,
            STARRED_FILTERS.REPOSITORIES,
            STARRED_FILTERS.PROJECTS,
          ],
          filters: [
            {
              key: STARRED_FILTERS.ALL,
              label: 'All',
              elemBefore: <AlignTextLeftIcon label="" />,
            },
            {
              key: STARRED_FILTERS.REPOSITORIES,
              label: 'Repositories',
              elemBefore: <BookWithBookmarkIcon label="" />,
            },
            {
              key: STARRED_FILTERS.PROJECTS,
              label: 'Projects',
              elemBefore: <FolderOpenIcon label="" />,
            },
          ],
        },
        useItems: params => {
          const searchText = params?.searchText;
          const selectedFilter = getSelectedStarredObjectType(
            params?.selectedFilters
          );

          const selectedFilterValue =
            params?.selectedFilters?.[0] ?? STARRED_FILTERS.ALL;
          const isInitialRender = useRef(true);

          useEffect(() => {
            if (isInitialRender.current) {
              isInitialRender.current = false;
              return;
            }
            publishUiEvent({
              action: 'clicked',
              actionSubject: 'filter',
              actionSubjectId: 'starredObjectsFilter',
              source: 'sideNav',
              attributes: {
                filterValue: selectedFilterValue,
                location: 'starredFlyout',
              },
            });
          }, [selectedFilterValue]);

          const {
            items,
            isLoading,
            hasMore,
            loadMore,
            unstarItem,
            isUnstarring,
          } = useStarredFlyoutItems(selectedFilter, searchText);

          const mapItem = (item: {
            type: 'repository' | 'project';
            resource: BB.Repository | BB.Project;
          }) => {
            const { resource, type } = item;
            const objectType: StarredObjectType =
              type === 'repository' ? 'repositories' : 'projects';
            return {
              primaryLabel: resource.name,
              href: (resource.links?.html?.href ?? '').replace(
                settings().CANON_URL,
                ''
              ),
              elemBefore: (
                <ContainerAvatar src={resource.links?.avatar?.href ?? ''} />
              ),
              elemAfter: (
                <IconButton
                  icon={() => (
                    <StarStarredIcon
                      label=""
                      color={token('color.icon.accent.yellow')}
                    />
                  )}
                  label="Remove from starred"
                  appearance="subtle"
                  spacing="compact"
                  isLoading={isUnstarring(resource.uuid)}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    publishUiEvent({
                      action: 'clicked',
                      actionSubject: 'button',
                      actionSubjectId: 'starButton',
                      source: 'sideNav',
                      attributes: {
                        objectType: type,
                        starAction: 'unstar',
                        location: 'starredFlyout',
                      },
                    });
                    unstarItem(objectType, resource);
                  }}
                />
              ),
            };
          };

          return {
            isLoading,
            hasMore,
            loadMore,
            sections: [{ items: items.map(mapItem) }],
          };
        },
      },
    }),
  };
};

const SideNavContent = (): JSX.Element => {
  const { pathname } = useLocation();
  const menuItems = useMenuItems();
  const { showStarredObjects, showStarredObjectsSpotlight } =
    useShowStarredObjects();
  const [isOnboardingActive, dismissOnboarding] = useStarredOnboarding(
    showStarredObjectsSpotlight
  );
  // Only authenticated users with the feature enabled can customize and persist
  // a side-nav view mode, so skip preference hydration entirely otherwise.
  // `showStarredObjects` blocks the preference lookup if false.
  const [
    repositoriesViewMode,
    setRepositoriesViewMode,
    isRepositoriesViewModeHydrating,
  ] = useOptimisticSideNavViewMode(
    REPOSITORIES_VIEW_MODE_PREFERENCE_KEY,
    showStarredObjects
  );
  const [projectsViewMode, setProjectsViewMode, isProjectsViewModeHydrating] =
    useOptimisticSideNavViewMode(
      PROJECTS_VIEW_MODE_PREFERENCE_KEY,
      showStarredObjects
    );
  const {
    isRepositoriesLoading: isRecentRepositoriesLoading,
    isProjectsLoading: isRecentProjectsLoading,
  } = useRecentData();
  const {
    starredItems: starredRepositories,
    recentItems: recentRepositories,
    isRecentsLoading: isRepositoryRecentsLoading,
    isStarredLoading: isStarredRepositoriesLoading,
  } = useRepositoryNavigation(repositoriesViewMode);
  const {
    starredItems: starredProjects,
    recentItems: recentProjects,
    isRecentsLoading: isProjectRecentsLoading,
    isStarredLoading: isStarredProjectsLoading,
  } = useProjectNavigation(projectsViewMode);
  const { loadRepositoriesOnce, loadProjectsOnce } = useLoadRecents();

  const allRepositoryItems = [...starredRepositories, ...recentRepositories];
  const allProjectItems = [...starredProjects, ...recentProjects];
  // Keep active/expanded state driven by the full navigation data even while we
  // temporarily hide view-mode-sensitive children during preference hydration.
  const displayedStarredRepositories = isRepositoriesViewModeHydrating
    ? []
    : starredRepositories;
  const displayedRecentRepositories = isRepositoriesViewModeHydrating
    ? []
    : recentRepositories;
  const displayedStarredProjects = isProjectsViewModeHydrating
    ? []
    : starredProjects;
  const displayedRecentProjects = isProjectsViewModeHydrating
    ? []
    : recentProjects;

  const isRepositoriesActive =
    isRepositoryRecentsLoading ||
    allRepositoryItems.some(repo => repo.isActive);
  const isProjectsActive =
    isProjectRecentsLoading ||
    allProjectItems.some(project => project.isActive);

  const activeItems: string[] = [];
  if (isRepositoriesActive) activeItems.push(REPOS_MENU_ITEM);
  if (isProjectsActive) activeItems.push(PROJECTS_MENU_ITEM);
  const { set, add, remove } = useSetState(activeItems);
  useEffect(() => {
    if (isRepositoriesActive) {
      add(REPOS_MENU_ITEM);
    }
    if (isProjectsActive) {
      add(PROJECTS_MENU_ITEM);
    }
  }, [isRepositoriesActive, isProjectsActive, add]);

  // If either of projects or repositories is active on mount, we want to load
  // their recent data.
  useEffect(() => {
    if (isRepositoriesActive) loadRepositoriesOnce();
  }, [isRepositoriesActive, loadRepositoriesOnce]);
  useEffect(() => {
    if (isProjectsActive) loadProjectsOnce();
  }, [isProjectsActive, loadProjectsOnce]);

  let overviewMenuItem: MenuItem | undefined;
  const items: ItemsConfig = {};
  menuItems?.main.forEach(item => {
    if (item.id === 'overview-tab') {
      overviewMenuItem = item;
      return;
    }
    if (item.id === 'repositories-tab') {
      items[item.id] = {
        type: 'expand',
        elemBefore: getIconForMenuItem(item),
        label: item.label,
        children: (
          <RepositoryNavigationItem
            allItemsUrl={item.url}
            starredItems={displayedStarredRepositories}
            recentItems={displayedRecentRepositories}
            isRecentsLoading={
              isRepositoriesViewModeHydrating
                ? false
                : isRecentRepositoriesLoading
            }
            isStarredLoading={
              isRepositoriesViewModeHydrating
                ? false
                : isStarredRepositoriesLoading
            }
            viewMode={repositoriesViewMode}
            isOnboardingActive={isOnboardingActive}
            dismissOnboarding={dismissOnboarding}
          />
        ),
        actionsOnHover: showStarredObjects ? (
          <SideNavViewModeDropdownMenu
            groupId="side-nav-repositories-view-mode"
            objectType="repositories"
            viewMode={repositoriesViewMode}
            onSelect={setRepositoriesViewMode}
          />
        ) : undefined,
        isExpanded: set.has(REPOS_MENU_ITEM),
        onExpansionToggle: (isExpanded: boolean) => {
          if (isExpanded) {
            loadRepositoriesOnce();
            add(REPOS_MENU_ITEM);
          } else {
            remove(REPOS_MENU_ITEM);
          }
        },
      };
      return;
    }

    if (item.id === 'projects-tab') {
      items[item.id] = {
        type: 'expand',
        elemBefore: getIconForMenuItem(item),
        label: item.label,
        children: (
          <ProjectNavigationItem
            allItemsUrl={item.url}
            starredItems={displayedStarredProjects}
            recentItems={displayedRecentProjects}
            isRecentsLoading={
              isProjectsViewModeHydrating ? false : isRecentProjectsLoading
            }
            isStarredLoading={
              isProjectsViewModeHydrating ? false : isStarredProjectsLoading
            }
            viewMode={projectsViewMode}
          />
        ),
        actionsOnHover: showStarredObjects ? (
          <SideNavViewModeDropdownMenu
            groupId="side-nav-projects-view-mode"
            objectType="projects"
            viewMode={projectsViewMode}
            onSelect={setProjectsViewMode}
          />
        ) : undefined,
        isExpanded: set.has(PROJECTS_MENU_ITEM),
        onExpansionToggle: (isExpanded: boolean) => {
          if (isExpanded) {
            loadProjectsOnce();
            add(PROJECTS_MENU_ITEM);
          } else {
            remove(PROJECTS_MENU_ITEM);
          }
        },
      };
      return;
    }

    items[item.id] = {
      type: 'link',
      elemBefore: getIconForMenuItem(item),
      label: item.label,
      isSelected: isMenuItemSelected(item, pathname),
      // Due to COREX-12399, we're using an absolute URL to force a non-SPA navigation
      // when pages are not yet supported in Frontbucket (neither fully nor via iframe)
      href: item.is_client_link
        ? item.url
        : new URL(item.url, settings().CANON_URL).toString(),
    };
  });

  menuItems?.secondary
    .filter(item => item.type === 'menu_item')
    .forEach(item => {
      items[item.id] = {
        type: 'link',
        elemBefore: getIconForMenuItem(item),
        label: item.label,
        isSelected: isMenuItemSelected(item, pathname),
        // Due to COREX-12399, we're using an absolute URL to force a non-SPA navigation
        // when pages are not yet supported in Frontbucket (neither fully nor via iframe)
        href: item.is_client_link
          ? item.url
          : new URL(item.url, settings().CANON_URL).toString(),
      };
    });

  const appMenuItems =
    menuItems?.secondary.filter(
      item =>
        item.type === 'connect_menu_item' || item.type === 'forge_menu_item'
    ) || [];
  if (appMenuItems.length > 0) {
    const { components, isSelected: isAnyAppItemSelected } = getAppMenuItems(
      appMenuItems,
      pathname
    );

    items.apps = {
      type: 'expand',
      elemBefore: <AppsIcon label="" />,
      label: 'Apps',
      children: <MenuList>{components}</MenuList>,
      isDefaultExpanded: isAnyAppItemSelected,
    };
  }

  return (
    <GlobalSideNavigation
      // Keeping this in here to be safe (prevents GSN from calling available products)
      // decide if we still need to pass this when working on COREX-12717
      isShortcutsStatic
      // COREX-12717 - At a later point before rollout we will decide what shortcuts to include for BBC
      // shortcuts={[]}
      app="bitbucket"
      appLabel="Bitbucket"
      forYou={forYouConfig({
        globalUrl: overviewMenuItem?.url ?? '/',
        isSelected: overviewMenuItem
          ? isMenuItemSelected(overviewMenuItem, pathname)
          : false,
        loadRepositories: loadRepositoriesOnce,
        allRecentItemsUrl: overviewMenuItem?.url ?? '/',
        showStarred: showStarredObjects,
      })}
      items={items}
      customizationStore="local"
    />
  );
};

/**
 * Wraps SideNavContent with StarredItemsProvider only when the starred feature
 * is active for the current user. When the provider is absent, hooks like
 * useStarredAndRecentItems will read `undefined` from StarredItemsContext and
 * gracefully fall back to showing only recent items.
 */
export const DefaultSideNav = (): JSX.Element => {
  const { showStarredObjects } = useShowStarredObjects();

  return showStarredObjects ? (
    <StarredItemsProvider>
      <SideNavContent />
    </StarredItemsProvider>
  ) : (
    <SideNavContent />
  );
};
